import { boolean, mixed, number, object, string, date } from 'yup';
import { generateNamesObject } from '@/utils';
import { FormValues, getInitialFormValues } from '@/utils';
import { OptionLeg } from './controls';
import {
    AlgoStrategy,
    MutualFundCapitalGains,
    MutualFundDividends,
    OptionStrategy,
    OrderFillType,
    OrderMarketTime,
    OrderTransaction,
    OrderType,
    OrderValidity,
    PositionType,
    TradeDestination,
    TrailingStopType,
} from './lookups';
import { SecurityType } from '@orbis-systems/orbis-api-middleware';
import { TRADE_DESTINATION_OPTIONS } from './selectOptions';

export const DEFAULT_LEG: OptionLeg = {
    call: true,
    strikePrice: 0,
    expirationDate: '',
    lastPrice: 0,
    bid: 0,
    ask: 0,
    symbol: '',
    volume: 0,
    action: OrderTransaction.Buy,
};

export const fieldNames = generateNamesObject(
    'symbol',
    'tradeDestination',
    'accountOrModel',
    'securityType',
    'optionLegs',
    'contracts',
    'transaction',
    'orderType',
    'stopPrice',
    'limitPrice',
    'quantity',
    'notionalValue',
    'fillType',
    'validity',
    'marketTime',
    'positionType',
    'mutualFundValue',
    'mutualFundCapitalGains',
    'mutualFundDividends',
    'accountId',
    'modelId',
    'orderValue',
    'trailingStopPegType',
    'trailingStopPegDifference',
    'optionStrategy',
    'spreadPrice',
    'price',
    'ocoOrder',
    'ocoQuantity',
    'ocoSymbol',
    'ocoLimitPrice',
    'ocoStopPrice',
    'optionRootSymbol',
    'algoStrategy',
    'algoStartTime',
    'algoEndTime',
    'volumeConstraint',
    'expireTime',
);

export const tradingFormSchema = object({
    [fieldNames.symbol]: string().required().label('Symbol').default(''),
    [fieldNames.tradeDestination]: mixed<TradeDestination>()
        .required()
        .label('Destination')
        .default(TradeDestination.SingleAccount),
    [fieldNames.accountOrModel]: string().required('Please select account or model').default(''),
    [fieldNames.securityType]: mixed<SecurityType>().required().label('Security Type').default(SecurityType.Equity),
    [fieldNames.optionLegs]: mixed<OptionLeg[]>().default([DEFAULT_LEG]),
    [fieldNames.contracts]: number()
        .integer()
        .label('Contracts')
        .default(1)
        .when([fieldNames.securityType, fieldNames.orderType], {
            is: (securityType: SecurityType) => securityType === SecurityType.Option,
            then: (schema) => schema.required().min(1),
            otherwise: (schema) => schema.positive().min(1),
        }),
    [fieldNames.transaction]: mixed<OrderTransaction>()
        .label('Transaction')
        .when(fieldNames.optionStrategy, {
            is: (value: OptionStrategy) => value !== OptionStrategy.BuyWrite,
            then: (schema) => schema.required(),
        }),
    [fieldNames.orderType]: mixed<OrderType>()
        .label('Order Type')
        .default(OrderType.Limit)
        .required()
        .when(fieldNames.securityType, {
            is: (securityType: SecurityType) =>
                securityType === SecurityType.MutualFund || securityType === SecurityType.Bond,
            then: (schema) => schema.default(OrderType.Market),
        }),
    [fieldNames.stopPrice]: number()
        .label('Stop Price')
        .positive()
        .when(fieldNames.orderType, {
            is: (value: OrderType) => value === OrderType.Stop || value === OrderType.StopLimit,
            then: (schema) => schema.positive().required(),
        }),
    [fieldNames.limitPrice]: number()
        .label('Limit Price')
        .positive()
        .when([fieldNames.orderType, fieldNames.securityType], {
            is: (orderType: OrderType, securityType: SecurityType) =>
                (orderType === OrderType.Limit || orderType === OrderType.StopLimit) &&
                securityType !== SecurityType.Bond &&
                securityType !== SecurityType.MutualFund,
            then: (schema) => schema.positive().required(),
        }),
    [fieldNames.validity]: mixed<OrderValidity>()
        .label('Validity')
        .when(fieldNames.securityType, {
            is: (securityType: SecurityType) => securityType !== SecurityType.Algo,
            then: (schema) => schema.required().default(OrderValidity.GoodForDay),
        }),
    [fieldNames.marketTime]: mixed<OrderMarketTime>()
        .label('Market Time')
        .when(fieldNames.securityType, {
            is: (securityType: SecurityType) => securityType !== SecurityType.Algo,
            then: (schema) => schema.required().default(OrderMarketTime.CoreMarket),
        }),
    [fieldNames.fillType]: mixed<OrderFillType>()
        .label('Fill Type')
        .when(fieldNames.securityType, {
            is: (securityType: SecurityType) => securityType !== SecurityType.Algo,
            then: (schema) => schema.required().default(OrderFillType.Any),
        }),
    [fieldNames.quantity]: number()
        .label('Quantity')
        .when([fieldNames.securityType, fieldNames.orderType], {
            is: (securityType: SecurityType, orderType: OrderType) =>
                (securityType === SecurityType.Equity || securityType === SecurityType.Algo) &&
                orderType !== OrderType.Notional,
            then: (schema) => schema.required().positive(),
        }),
    [fieldNames.notionalValue]: number()
        .label('Value')
        .positive()
        .when(fieldNames.orderType, {
            is: OrderType.Notional,
            then: (schema) => schema.required(),
        }),
    [fieldNames.positionType]: mixed<PositionType>().label('Position Type'),
    [fieldNames.optionRootSymbol]: mixed<string>().label('Root Symbol'),
    [fieldNames.mutualFundValue]: number()
        .label('Value')
        .when(fieldNames.securityType, {
            is: SecurityType.MutualFund,
            then: (schema) => schema.positive().required(),
        }),
    [fieldNames.mutualFundCapitalGains]: mixed<MutualFundCapitalGains>()
        .label('Capital Gains')
        .default(MutualFundCapitalGains.Reinvest),
    [fieldNames.mutualFundDividends]: mixed<MutualFundDividends>()
        .label('Dividends')
        .default(MutualFundDividends.Reinvest),
    [fieldNames.accountId]: number()
        .label('Account')
        .when(fieldNames.tradeDestination, {
            is: TRADE_DESTINATION_OPTIONS.singleAccount.value,
            then: (schema) => schema.required(),
        }),
    [fieldNames.modelId]: number()
        .label('Model')
        .when(fieldNames.tradeDestination, {
            is: TRADE_DESTINATION_OPTIONS.model.value,
            then: (schema) => schema.required(),
        }),
    [fieldNames.orderValue]: number().label('Order Value'),
    [fieldNames.trailingStopPegType]: mixed<TrailingStopType>()
        .label('Peg Type')
        .when(fieldNames.orderType, {
            is: (value: OrderType) => value === OrderType.TrailingStop,
            then: (schema) => schema.required(),
        }),
    [fieldNames.trailingStopPegDifference]: number()
        .label('Peg Difference')
        .when(fieldNames.orderType, {
            is: (value: OrderType) => value === OrderType.TrailingStop,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema,
        }),
    [fieldNames.optionStrategy]: mixed<OptionStrategy>()
        .label('Strategy')
        .default(OptionStrategy.SingleLeg)
        .when(fieldNames.securityType, {
            is: (value: SecurityType) => value === SecurityType.Option,
            then: (schema) => schema.required(),
        }),
    [fieldNames.spreadPrice]: number()
        .label('Spread Price')
        .when(fieldNames.orderType, {
            is: (value: OrderType) => value === OrderType.Credit || value === OrderType.Debit,
            then: (schema) => schema.positive().required(),
        }),
    [fieldNames.price]: number()
        .label('Price')
        .when(fieldNames.securityType, {
            is: (value: SecurityType) => value === SecurityType.Bond,
            then: (schema) => schema.positive().required(),
        }),
    [fieldNames.ocoOrder]: boolean().label('OCO Order').default(false),
    [fieldNames.ocoQuantity]: number()
        .label('OCO Quantity')
        .when([fieldNames.ocoOrder, fieldNames.transaction], {
            is: (ocoOrder: boolean, transaction: OrderTransaction) =>
                ocoOrder === true && transaction === OrderTransaction.Buy,
            then: (schema) => schema.required(),
        }),
    [fieldNames.ocoSymbol]: string()
        .label('OCO Symbol')
        .when([fieldNames.ocoOrder, fieldNames.transaction], {
            is: (ocoOrder: boolean, transaction: OrderTransaction) =>
                ocoOrder === true && transaction === OrderTransaction.Buy,
            then: (schema) => schema.required(),
        }),
    [fieldNames.ocoLimitPrice]: number()
        .label('OCO Limit Price')
        .when([fieldNames.ocoOrder, fieldNames.orderType, fieldNames.transaction], {
            is: (ocoOrder: boolean, orderType: OrderType, transaction: OrderTransaction) =>
                ocoOrder === true &&
                ((orderType === OrderType.Limit && transaction === OrderTransaction.Buy) ||
                    (orderType === OrderType.Stop && transaction === OrderTransaction.Sell)),
            then: (schema) => schema.positive().required(),
        }),
    [fieldNames.ocoStopPrice]: number()
        .label('OCO Stop Price')
        .when([fieldNames.ocoOrder, fieldNames.orderType, fieldNames.transaction], {
            is: (ocoOrder: boolean, orderType: OrderType, transaction: OrderTransaction) =>
                ocoOrder === true && orderType === OrderType.Limit && transaction === OrderTransaction.Sell,
            then: (schema) => schema.required(),
        }),
    [fieldNames.algoStrategy]: mixed<AlgoStrategy>()
        .label('Algo Strategy')
        .default(AlgoStrategy.VWAP)
        .when(fieldNames.securityType, {
            is: (securityType: SecurityType) => securityType === SecurityType.Algo,
            then: (schema) => schema.required(),
        }),
    [fieldNames.algoStartTime]: date()
        .label('Start Time')
        .when(fieldNames.securityType, {
            is: (securityType: SecurityType) => securityType === SecurityType.Algo,
            then: (schema) => schema.required(),
        }),
    [fieldNames.algoEndTime]: date()
        .label('End Time')
        .when(fieldNames.securityType, {
            is: (securityType: SecurityType) => securityType === SecurityType.Algo,
            then: (schema) => schema.required(),
        }),
    [fieldNames.volumeConstraint]: number()
        .label('Volume Constraint')
        .when([fieldNames.securityType, fieldNames.algoStrategy], {
            is: (securityType: SecurityType, algoStrategy: AlgoStrategy) =>
                securityType === SecurityType.Algo && algoStrategy === AlgoStrategy.POV,
            then: (schema) => schema.required(),
        }),
    [fieldNames.expireTime]: date()
        .label('Expiration')
        .when(fieldNames.validity, {
            is: (validity: OrderValidity) => validity === OrderValidity.GoodTilDate,
            then: (schema) => schema.required(),
        }),
});

export type TradingFormValues = FormValues<typeof tradingFormSchema>;

export const getInitialTradingFormValues = (values?: Partial<TradingFormValues>) =>
    getInitialFormValues(tradingFormSchema, values);
